import { useId } from 'react';

interface UserBgShapeProps {
  imageUrl?: string | null;
  /** Cyan stroke colour. Defaults to the brand colour. */
  strokeColor?: string;
  /** Inner glow/gradient when no banner image is set. */
  fallbackFrom?: string;
  fallbackTo?: string;
  className?: string;
}

/**
 * Esports banner clip — direct port of `mobile-app/src/components/tournaments/user-banner.tsx`
 * UserBg SVG. The right edge folds inward into a chevron (cubic Bezier "swoosh"),
 * the left is squared with 4px rounded corners. The image is masked inside; the
 * cyan stroke runs along the outline.
 *
 * Sizing: viewBox 0 0 395 99 — a 4:1-ish ratio. The container's width drives the
 * scale; height auto-derived via `aspectRatio: 395 / 99`.
 *
 * Each instance gets a unique <clipPath> id via React's `useId` so multiple
 * banners on the same page don't trample each other's masks.
 */
export function UserBgShape({
  imageUrl,
  // Defaults reference the theme CSS vars so the banner tracks the active
  // theme (accent stroke + a no-image gradient that fades to the page bg).
  strokeColor = 'var(--color-brand)',
  fallbackFrom = '#0F4F4C',
  fallbackTo = 'var(--color-bg)',
  className,
}: UserBgShapeProps) {
  const rawId = useId();
  const clipId = `userbg-clip-${rawId}`;
  const gradId = `userbg-grad-${rawId}`;

  // Path for the clip mask (filled shape used to crop the image / gradient).
  const maskPath =
    'M0 4a4 4 0 014-4h380c2.947 0 4.882 3.079 3.604 5.735l-19.722 40.98a4 4 0 00-.029 3.408l19.898 43.204c1.221 2.65-.715 5.673-3.633 5.673H4a4 4 0 01-4-4V4z';
  // Inset stroke path — matches the mask but offset inward so the stroke
  // doesn't get clipped at the edges of the viewBox.
  const strokePath =
    'M4 2.5h380a1.5 1.5 0 011.351 2.15l-19.722 40.98a6.502 6.502 0 00-.047 5.539l19.898 43.204a1.5 1.5 0 01-1.362 2.127H4A1.5 1.5 0 012.5 95V4A1.5 1.5 0 014 2.5z';

  return (
    <svg
      viewBox="0 0 395 99"
      preserveAspectRatio="none"
      className={className}
      aria-hidden
    >
      <defs>
        <clipPath id={clipId}>
          <path d={maskPath} />
        </clipPath>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={fallbackFrom} />
          <stop offset="100%" stopColor={fallbackTo} />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {imageUrl ? (
          <image
            href={imageUrl}
            x="0"
            y="0"
            width="395"
            height="99"
            preserveAspectRatio="xMidYMid slice"
          />
        ) : (
          <rect x="0" y="0" width="395" height="99" fill={`url(#${gradId})`} />
        )}
        {/* Subtle inner shade for legibility regardless of image */}
        <rect
          x="0"
          y="0"
          width="395"
          height="99"
          fill="rgba(0,0,0,0.25)"
        />
      </g>
      <path
        d={strokePath}
        fill="none"
        stroke={strokeColor}
        strokeWidth="3"
        strokeLinejoin="miter"
        strokeMiterlimit="10"
      />
    </svg>
  );
}
